import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
// eslint-disable-next-line
import MicIcon from '@mui/icons-material/Mic';
// eslint-disable-next-line
import MicOffIcon from '@mui/icons-material/MicOff';
// eslint-disable-next-line
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
// eslint-disable-next-line
import ChatIcon from '@mui/icons-material/Chat';
import DeleteIcon from '@mui/icons-material/Delete';
import HeadsetIcon from '@mui/icons-material/Headset';
import env from '../../utils/env';
import './TabAudioWhisper.css';

function TabAudioWhisper() {
  // State variables
  const [isCapturing, setIsCapturing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [gptResponse, setGptResponse] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);
  // eslint-disable-next-line
  const [continuousRecording, setContinuousRecording] = useState(false);

  // Refs
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const lastProcessedTextRef = useRef('');
  const continuousTimerRef = useRef(null);

  // Clean up resources when component unmounts
  useEffect(() => {
    return () => {
      stopCapture();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (continuousTimerRef.current) {
        clearInterval(continuousTimerRef.current);
      }
    };
  }, []);

  // Start tab audio capture with continuous Whisper transcription
  const startCapture = async () => {
    try {
      setErrorMessage('');
      
      // Request screen sharing with audio
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true
      });
      
      // Check if audio is included
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        throw new Error("No audio track found. Please make sure to select 'Share audio' when sharing the tab.");
      }
      
      console.log("Tab audio capture started with tracks:", 
        stream.getTracks().map(t => `${t.kind} (${t.label})`));
      
      // Store the stream for cleanup
      mediaStreamRef.current = stream;
      
      // Start continuous recording with Whisper
      startContinuousRecording();
      
      // Set capturing state
      setIsCapturing(true);
      
      // Handle stream ending (user stops sharing)
      stream.getVideoTracks()[0].onended = () => {
        stopCapture();
      };
      
    } catch (error) {
      console.error("Error capturing tab audio:", error);
      setErrorMessage(`Failed to capture tab audio: ${error.message}`);
    }
  };

  // Stop tab audio capture
  const stopCapture = () => {
    // Stop continuous recording
    stopContinuousRecording();

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
    }
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    setIsCapturing(false);
    setIsRecording(false);
    audioChunksRef.current = [];
  };

  // Start continuous recording with Whisper transcription
  const startContinuousRecording = () => {
    if (!mediaStreamRef.current) {
      setErrorMessage("No tab audio stream available.");
      return;
    }
    
    try {
      // Check if MediaRecorder is supported
      if (typeof MediaRecorder === 'undefined') {
        throw new Error("MediaRecorder API is not supported in this browser");
      }
      
      // Find supported MIME type
      const mimeTypes = [
        'audio/webm',
        'audio/webm;codecs=opus',
        'audio/ogg;codecs=opus',
        'audio/mp4',
        'audio/wav',
        ''  // Empty string means browser's default
      ];
      
      let selectedMimeType = '';
      for (const type of mimeTypes) {
        const isSupported = !type || MediaRecorder.isTypeSupported(type);
        if (isSupported) {
          selectedMimeType = type;
          break;
        }
      }
      
      console.log(`Using MIME type: ${selectedMimeType || 'browser default'}`);
      
      // Create audio-only stream
      const audioStream = new MediaStream();
      mediaStreamRef.current.getAudioTracks().forEach(track => {
        audioStream.addTrack(track);
      });
      
      if (audioStream.getAudioTracks().length === 0) {
        throw new Error("No audio tracks found in the stream. Make sure you checked 'Share audio' when sharing the tab.");
      }
      
      // Set up continuous recording
      setContinuousRecording(true);
      audioChunksRef.current = [];
      
      // Create new recorder
      const options = selectedMimeType ? { mimeType: selectedMimeType } : {};
      const mediaRecorder = new MediaRecorder(audioStream, options);
      mediaRecorderRef.current = mediaRecorder;
      
      // Set up data handling
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      // Start recording with 1-second data requests
      mediaRecorder.start(1000);
      console.log("Continuous recording started");
      
      // Set up timer to process audio every 5 seconds
      continuousTimerRef.current = setInterval(async () => {
        if (audioChunksRef.current.length > 0) {
          // Process the audio with Whisper
          await processAudioWithWhisper();
          // Clear chunks for next segment
          audioChunksRef.current = [];
        }
      }, 5000);
      
    } catch (error) {
      console.error("Error starting continuous recording:", error);
      setErrorMessage(`Failed to start continuous recording: ${error.message}`);
      setContinuousRecording(false);
    }
  };

  // Stop continuous recording
  const stopContinuousRecording = () => {
    setContinuousRecording(false);
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    if (continuousTimerRef.current) {
      clearInterval(continuousTimerRef.current);
      continuousTimerRef.current = null;
    }
  };

  // Process audio with Whisper API for continuous recording
  const processAudioWithWhisper = async () => {
    if (audioChunksRef.current.length === 0) {
      return;
    }
    
    try {
      // Get API key from environment variables
      const apiKey = env.OPENAI_API_KEY;
      if (!apiKey) {
        throw new Error("OpenAI API key not found. Please add it to your .env file as REACT_APP_OPENAI_API_KEY.");
      }
      
      // Create audio blob
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      
      // Skip if the blob is too small (likely no audio)
      if (audioBlob.size < 1000) {
        console.log("Audio blob too small, skipping:", audioBlob.size);
        return;
      }
      
      console.log(`Processing audio blob: ${audioBlob.size} bytes`);
      
      // Create form data for API request
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.webm');
      formData.append('model', 'whisper-1');
      
      // Send to OpenAI Whisper API
      const transcriptionResponse = await axios.post(
        'https://api.openai.com/v1/audio/transcriptions',
        formData,
        {
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const transcriptionText = transcriptionResponse.data.text;
      
      // Only update if we got meaningful text
      if (transcriptionText && transcriptionText.trim()) {
        console.log("Whisper Transcription:", transcriptionText);
        setTranscription(prev => prev ? `${prev}\n${transcriptionText}` : transcriptionText);
        
        // Process for AI response if it seems like a question
        processTextForAI(transcriptionText);
      }
      
    } catch (error) {
      console.error("Error processing audio with Whisper:", error);
      // Don't show error message for every segment to avoid flooding the UI
      if (error.response?.status === 401) {
        setErrorMessage("Invalid OpenAI API key. Please check your API key and try again.");
        stopContinuousRecording();
      }
    }
  };

  // Process text for AI response
  const processTextForAI = (text) => {
    // Skip if the text is too similar to the last processed text
    if (text === lastProcessedTextRef.current) {
      return;
    }
    
    // Check if the text contains a question or appears to need a response
    const isQuestion = text.includes('?') || 
                       /^(what|how|why|when|where|who|can|could|would|should|is|are|do|does|did|will|has|have)/.test(text.toLowerCase());
    
    if (isQuestion) {
      sendToGPT(text);
      lastProcessedTextRef.current = text;
    }
  };

  // Start recording a 5-second audio clip (keeping this for the manual clip feature)
  const startRecording = () => {
    if (!mediaStreamRef.current) {
      setErrorMessage("No tab audio stream available. Please start tab audio capture first.");
      return;
    }
    
    try {
      // Check if MediaRecorder is supported
      if (typeof MediaRecorder === 'undefined') {
        throw new Error("MediaRecorder API is not supported in this browser");
      }
      
      // Log available media tracks for debugging
      const tracks = mediaStreamRef.current.getTracks();
      console.log("Available tracks for recording:", 
        tracks.map(t => `${t.kind} (${t.label}): enabled=${t.enabled}, muted=${t.muted}`));
      
      // Reset state
      setIsRecording(true);
      setRecordingTime(0);
      audioChunksRef.current = [];
      
      // Check for supported MIME types
      const mimeTypes = [
        'audio/webm',
        'audio/webm;codecs=opus',
        'audio/ogg;codecs=opus',
        'audio/mp4',
        'audio/wav',
        ''  // Empty string means browser's default
      ];
      
      console.log("Testing MIME type support:");
      let selectedMimeType = '';
      
      // Find the first supported MIME type
      for (const type of mimeTypes) {
        const isSupported = !type || MediaRecorder.isTypeSupported(type);
        console.log(`- ${type || 'browser default'}: ${isSupported ? 'supported' : 'not supported'}`);
        if (isSupported) {
          selectedMimeType = type;
          break;
        }
      }
      
      if (!selectedMimeType) {
        console.warn("No specified MIME types are supported, using browser default");
      }
      
      console.log(`Using MIME type: ${selectedMimeType || 'browser default'}`);
      
      // Create media recorder with supported type
      const options = selectedMimeType ? { mimeType: selectedMimeType } : {};
      
      // Add audio constraints to ensure we're getting audio
      const audioStream = new MediaStream();
      mediaStreamRef.current.getAudioTracks().forEach(track => {
        audioStream.addTrack(track);
      });
      
      if (audioStream.getAudioTracks().length === 0) {
        throw new Error("No audio tracks found in the stream. Make sure you checked 'Share audio' when sharing the tab.");
      }
      
      const mediaRecorder = new MediaRecorder(audioStream, options);
      mediaRecorderRef.current = mediaRecorder;
      
      // Set up data handling
      mediaRecorder.ondataavailable = (event) => {
        console.log(`Data available event: size=${event.data.size} bytes, type=${event.data.type}`);
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      // Handle recording stop
      mediaRecorder.onstop = async () => {
        console.log("MediaRecorder stopped");
        setIsRecording(false);
        if (audioChunksRef.current.length > 0) {
          console.log(`Processing ${audioChunksRef.current.length} audio chunks`);
          await processAudio();
        } else {
          console.warn("No audio data collected");
          setErrorMessage("No audio data was recorded. Please try again and ensure audio is playing.");
        }
      };
      
      // Handle recording errors
      mediaRecorder.onerror = (event) => {
        console.error("MediaRecorder error:", event);
        setErrorMessage(`Recording error: ${event.error}`);
        setIsRecording(false);
      };
      
      // Request data every second to ensure we get something
      mediaRecorder.start(1000);
      console.log("MediaRecorder started");
      
      // Set up timer for 5 seconds
      let timeElapsed = 0;
      timerRef.current = setInterval(() => {
        timeElapsed += 1;
        setRecordingTime(timeElapsed);
        
        // Stop after 5 seconds
        if (timeElapsed >= 5) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          
          if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            console.log("Stopping MediaRecorder after 5 seconds");
            mediaRecorderRef.current.stop();
          }
        }
      }, 1000);
      
    } catch (error) {
      console.error("Error starting recording:", error);
      setErrorMessage(`Failed to start recording: ${error.message}`);
      setIsRecording(false);
    }
  };

  // Process the recorded audio (for manual clip feature)
  const processAudio = async () => {
    if (audioChunksRef.current.length === 0) {
      setErrorMessage("No audio recorded.");
      return;
    }
    
    setIsProcessing(true);
    setErrorMessage('');
    
    try {
      // Get API key from environment variables
      const apiKey = env.OPENAI_API_KEY;
      if (!apiKey) {
        throw new Error("OpenAI API key not found. Please add it to your .env file as REACT_APP_OPENAI_API_KEY.");
      }
      
      // Create audio blob
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      console.log(`Audio blob created: ${audioBlob.size} bytes`);
      
      // Create form data for API request
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.webm');
      formData.append('model', 'whisper-1');
      
      // Send to OpenAI Whisper API
      const transcriptionResponse = await axios.post(
        'https://api.openai.com/v1/audio/transcriptions',
        formData,
        {
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const transcriptionText = transcriptionResponse.data.text;
      setTranscription(prev => prev ? `${prev}\n${transcriptionText}` : transcriptionText);
      console.log("Whisper Transcription:", transcriptionText);
      
      // If we have a transcription, send to GPT
      if (transcriptionText.trim()) {
        await sendToGPT(transcriptionText);
      }
      
    } catch (error) {
      console.error("Error processing audio:", error);
      setErrorMessage(`Failed to process audio: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Send transcription to GPT
  const sendToGPT = async (text) => {
    try {
      setIsProcessing(true);
      
      // Get API key from environment variables
      const apiKey = env.OPENAI_API_KEY;
      if (!apiKey) {
        throw new Error("OpenAI API key not found. Please add it to your .env file as REACT_APP_OPENAI_API_KEY.");
      }
      
      const response = await axios.post(
        'https://api.openai.com/v1/chat/completions',
        {
          model: "gpt-4.1-nano",
          messages: [
            {
              role: "system",
              content: "You are an AI assistant responding to transcribed audio from a meeting or video. Keep your responses concise, helpful, and conversational. If the input is a question, provide a direct answer. If it's a statement that doesn't require a response, you can acknowledge it briefly or provide relevant information."
            },
            {
              role: "user",
              content: text
            }
          ]
        },
        {
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const gptText = response.data.choices[0].message.content;
      setGptResponse(gptText);
      console.log("GPT Response:", gptText);
      
    } catch (error) {
      console.error("Error sending to GPT:", error);
      setErrorMessage(`Failed to get GPT response: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Clear results
  const clearResults = () => {
    setTranscription('');
    setInterimTranscript('');
    setGptResponse('');
    setErrorMessage('');
  };

  return (
    <div className="tab-audio-whisper">
      <div className="main-content">
        <div className="capture-section">
          <div className="section-header">
            <h3><HeadsetIcon className="header-icon" /> Tab Audio Capture</h3>
            <button className="clear-button" onClick={clearResults}>
              <DeleteIcon fontSize="small" /> Clear
            </button>
          </div>
          
          <div className="capture-controls">
            <button 
              className={`capture-button ${isCapturing ? 'capturing' : ''}`}
              onClick={isCapturing ? stopCapture : startCapture}
              disabled={isProcessing}
            >
              {isCapturing ? 'Stop Tab Capture' : 'Start Tab Capture'}
            </button>
            
            {isCapturing && (
              <button 
                className={`record-button ${isRecording ? 'recording' : ''}`}
                onClick={startRecording}
                disabled={isRecording || isProcessing}
              >
                Record 5s Clip
              </button>
            )}
          </div>
          
          {isRecording && (
            <div className="recording-indicator">
              Recording: {recordingTime}s / 5s
              <div className="recording-progress" style={{ width: `${(recordingTime / 5) * 100}%` }}></div>
            </div>
          )}
          
          {isProcessing && (
            <div className="processing-indicator">
              Processing audio...
            </div>
          )}
          
          {errorMessage && (
            <div className="error-message">
              {errorMessage}
            </div>
          )}
          
          <div className="transcription-container">
            <h4>Transcription:</h4>
            <div className="transcription-content">
              {transcription ? (
                <div className="transcription-text">{transcription}</div>
              ) : (
                <div className="placeholder-text">
                  Captured audio will be transcribed here...
                </div>
              )}
              {interimTranscript && (
                <div className="interim-text">{interimTranscript}</div>
              )}
            </div>
          </div>
        </div>
        
        <div className="response-section">
          <div className="section-header">
            <h3><SmartToyIcon className="header-icon" /> AI Response</h3>
          </div>
          
          <div className="response-container">
            {gptResponse ? (
              <div className="response-text">{gptResponse}</div>
            ) : (
              <div className="placeholder-text">
                AI response will appear here after transcription...
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="instructions">
        <h4>How to use:</h4>
        <ol>
          <li>Click "Start Tab Capture" and select a browser tab with audio (YouTube, Google Meet, etc.)</li>
          <li>Make sure to check "Share audio" in the browser dialog</li>
          <li>Audio will be continuously recorded in 5-second segments and sent to OpenAI's Whisper API</li>
          <li>Transcriptions will appear in real-time</li>
          <li>Questions detected in the audio will automatically be sent to AI for responses</li>
        </ol>
      </div>
    </div>
  );
}

export default TabAudioWhisper;

